import { SyntheticEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { Box, Tooltip, Typography, useMediaQuery } from '@mui/material';
import { NoPhotography, Cancel, CheckCircle } from '@mui/icons-material';
import { Vehicle, VehicleFile } from 'types/vehicles/types';
import { USER_TYPE_ADMIN, USER_TYPE_WAREHOUSE } from '../../../types/auth/types';
import UserCard from 'pages/users/list/UserCard';
import { vehicleListItemStyles } from './styles';
import VehicleStatus from '../status/VehicleStatus';
import {
  STATUS_DELETED,
  VEHICLE_FUEL_TYPE_COLOR_MAP,
  MECHANICAL_STATUS_LABEL_COLOR_MAP,
  FILE_TYPE_BILL_OF_SALE,
  FILE_TYPE_INVOICE,
  FILE_TYPE_TITLE,
} from '../config';

// Fields visible to warehouse users
const WAREHOUSE_VISIBLE_FIELDS = [
  'pictures',
  'stock',
  'vin',
  'fuel_type',
  'vendor',
  'destination',
  'mechanical_status',
  'keys',
  'notes',
];

type Props = {
  vehicle: Vehicle;
  shortRepresentation?: boolean;
};

const VehicleListItem = ({ vehicle, shortRepresentation = false }: Props) => {
  const {
    id,
    name,
    status,
    cover_picture: coverPicture,
    files,
    account,
    stock,
    vin,
    fuel_type: fuelType,
    vendor,
    invoice_date: invoiceDate,
    client,
    location,
    container_number: containerNumber,
    destination,
    importer,
    technical_status: technicalStatus,
    keys,
    comment,
  } = vehicle;
  const navigate = useNavigate();

  const user = useSelector((state: RootState) => state.auth.user);
  const isMobileView = useMediaQuery('(max-width:600px)');
  const isVehicleDeleted = status.value === STATUS_DELETED;

  // Check if a field should be visible for the current user type
  const isFieldVisible = (fieldName: string): boolean => {
    if (!user?.user_type) return true;

    switch (user.user_type) {
      case USER_TYPE_WAREHOUSE:
        // Warehouse users can only see specific fields
        return WAREHOUSE_VISIBLE_FIELDS.includes(fieldName);
      default:
        // Default to showing all fields
        return true;
    }
  };

  // Check if any field in a list is visible
  const isAnyFieldVisible = (fieldNames: string[]): boolean => {
    return fieldNames.some(fieldName => isFieldVisible(fieldName));
  };

  const navigateToVehiclePage = (event: SyntheticEvent) => {
    // Go to vehicle page
    event.preventDefault();
    event.stopPropagation();
    navigate(`/home/vehicles/${id}`);
  };

  const getValueWithTooltip = (value: string, object: unknown, Card: React.ElementType) => {
    if (!value) {
      return '-';
    }
    if (user?.user_type !== USER_TYPE_ADMIN || isMobileView) {
      return value;
    }
    return (
      <Tooltip
        title={<Card option={object} showActionButtons={false} />}
        arrow
        componentsProps={{
          tooltip: {
            sx: {
              bgcolor: 'transparent',
              maxWidth: 'none',
            },
          },
        }}
      >
        <Typography>{value}</Typography>
      </Tooltip>
    );
  };

  return (
    <Box
      sx={
        isVehicleDeleted
          ? { ...vehicleListItemStyles.container, ...vehicleListItemStyles.deleted }
          : vehicleListItemStyles.container
      }
      onClick={navigateToVehiclePage}
    >
      <Box sx={vehicleListItemStyles.header}>
        <Box sx={vehicleListItemStyles.name}>{name}</Box>
        <VehicleStatus {...status} />
      </Box>
      <Box sx={vehicleListItemStyles.detailsOuterContainer}>
        <Box sx={vehicleListItemStyles.photoContainer}>
          {coverPicture ? (
            <img
              src={`${process.env.REACT_APP_MEDIA_API_URL}/${coverPicture.picture}`}
              alt={`${process.env.REACT_APP_MEDIA_API_URL}/${coverPicture.picture}`}
              loading="lazy"
              style={{
                width: '100%',
                maxWidth: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'center',
                borderRadius: '10px',
              }}
            />
          ) : (
            <NoPhotography sx={{ width: 30, height: 30 }} />
          )}
        </Box>
        <Box sx={vehicleListItemStyles.detailsContainer}>
          {isAnyFieldVisible(['account', 'stock']) && (
            <Box sx={{ ...vehicleListItemStyles.detailsColumn, flex: '1.2' }}>
              {isFieldVisible('account') && (
                <Box sx={vehicleListItemStyles.detailContainer}>
                  <Box sx={vehicleListItemStyles.detailTitle}>Account</Box>
                  <Box sx={vehicleListItemStyles.detailValue}>{account?.name || '-'}</Box>
                </Box>
              )}
              {isFieldVisible('stock') && (
                <Box sx={vehicleListItemStyles.detailContainer}>
                  <Box sx={vehicleListItemStyles.detailTitle}>Stock</Box>
                  <Box sx={vehicleListItemStyles.detailValue}>{stock || ''}</Box>
                </Box>
              )}
            </Box>
          )}
          <Box sx={{ ...vehicleListItemStyles.detailsColumn, flex: '1.3' }}>
            {isFieldVisible('vin') && (
              <Box sx={vehicleListItemStyles.detailContainer}>
                <Box sx={vehicleListItemStyles.detailTitle}>VIN</Box>
                <Box sx={vehicleListItemStyles.detailValue}>{vin || '-'}</Box>
              </Box>
            )}
            {isMobileView
              ? isFieldVisible('client') && (
                  <Box sx={vehicleListItemStyles.detailContainer}>
                    <Box sx={vehicleListItemStyles.detailTitle}>User</Box>
                    <Box sx={vehicleListItemStyles.detailValue}>
                      {client ? getValueWithTooltip(`${client.first_name} ${client.last_name}`, client, UserCard) : '-'}
                    </Box>
                  </Box>
                )
              : isFieldVisible('fuel_type') && (
                  <Box sx={vehicleListItemStyles.detailContainer}>
                    <Box sx={vehicleListItemStyles.detailTitle}>Fuel Type</Box>
                    <Box
                      sx={{
                        ...vehicleListItemStyles.detailValue,
                        color: VEHICLE_FUEL_TYPE_COLOR_MAP[fuelType] || '',
                      }}
                    >
                      {fuelType || '-'}
                    </Box>
                  </Box>
                )}
          </Box>
          {!isMobileView && isAnyFieldVisible(['invoice_date', 'client']) ? (
            <Box sx={{ ...vehicleListItemStyles.detailsColumn, flex: '1' }}>
              {isFieldVisible('invoice_date') && (
                <Box sx={vehicleListItemStyles.detailContainer}>
                  <Box sx={vehicleListItemStyles.detailTitle}>Invoice Date</Box>
                  <Box sx={vehicleListItemStyles.detailValue}>{invoiceDate || '-'}</Box>
                </Box>
              )}
              {isFieldVisible('client') && (
                <Box sx={vehicleListItemStyles.detailContainer}>
                  <Box sx={vehicleListItemStyles.detailTitle}>User</Box>
                  <Box sx={vehicleListItemStyles.detailValue}>
                    {client ? getValueWithTooltip(`${client.first_name} ${client.last_name}`, client, UserCard) : '-'}
                  </Box>
                </Box>
              )}
            </Box>
          ) : null}
          {!isMobileView && isAnyFieldVisible(['location', 'vendor']) ? (
            <Box sx={{ ...vehicleListItemStyles.detailsColumn, flex: '1' }}>
              {isFieldVisible('location') && (
                <Box sx={vehicleListItemStyles.detailContainer}>
                  <Box sx={vehicleListItemStyles.detailTitle}>Location</Box>
                  <Box sx={vehicleListItemStyles.detailValue}>{location?.name || '-'}</Box>
                </Box>
              )}
              {isFieldVisible('vendor') && (
                <Box sx={vehicleListItemStyles.detailContainer}>
                  <Box sx={vehicleListItemStyles.detailTitle}>Vendor</Box>
                  <Box sx={vehicleListItemStyles.detailValue}>{vendor || '-'}</Box>
                </Box>
              )}
            </Box>
          ) : null}
          {!isMobileView && isAnyFieldVisible(['container_number', 'destination']) ? (
            <Box sx={{ ...vehicleListItemStyles.detailsColumn, flex: '1' }}>
              {isFieldVisible('container_number') && (
                <Box sx={vehicleListItemStyles.detailContainer}>
                  <Box sx={vehicleListItemStyles.detailTitle}>Container #</Box>
                  <Box sx={vehicleListItemStyles.detailValue}>{containerNumber || '-'}</Box>
                </Box>
              )}
              {isFieldVisible('destination') && (
                <Box sx={vehicleListItemStyles.detailContainer}>
                  <Box sx={vehicleListItemStyles.detailTitle}>Destination</Box>
                  <Box sx={vehicleListItemStyles.detailValue}>{destination?.name || '-'}</Box>
                </Box>
              )}
            </Box>
          ) : null}
          {!shortRepresentation && !isMobileView && isAnyFieldVisible(['config_id', 'mechanical_status']) ? (
            <Box sx={{ ...vehicleListItemStyles.detailsColumn, flex: '1' }}>
              {isFieldVisible('config_id') && (
                <Box sx={vehicleListItemStyles.detailContainer}>
                  <Box sx={vehicleListItemStyles.detailTitle}>Config ID</Box>
                  <Box sx={vehicleListItemStyles.detailValue}>-</Box>
                </Box>
              )}
              {isFieldVisible('mechanical_status') && (
                <Box sx={vehicleListItemStyles.detailContainer}>
                  <Box sx={vehicleListItemStyles.detailTitle}>Mech Status</Box>
                  <Box
                    sx={{
                      ...vehicleListItemStyles.detailValue,
                      color:
                        technicalStatus.value !== undefined
                          ? MECHANICAL_STATUS_LABEL_COLOR_MAP[technicalStatus.value]
                          : 'inherit',
                    }}
                  >
                    {technicalStatus.display_name || '-'}
                  </Box>
                </Box>
              )}
            </Box>
          ) : null}
          {!shortRepresentation && !isMobileView && isFieldVisible('documents') ? (
            <>
              <Box sx={{ ...vehicleListItemStyles.detailsColumn, flex: '0.7' }}>
                <Box sx={vehicleListItemStyles.detailContainer}>
                  <Box sx={vehicleListItemStyles.detailTitle}>Bill of sale</Box>
                  <Box sx={vehicleListItemStyles.detailValue}>
                    {files.filter((file: VehicleFile) => file.type === FILE_TYPE_BILL_OF_SALE).length > 0 ? (
                      <CheckCircle sx={{ width: 20, height: 20 }} color="success" />
                    ) : (
                      <Cancel sx={{ width: 20, height: 20 }} color="error" />
                    )}
                  </Box>
                </Box>
                <Box sx={vehicleListItemStyles.detailContainer}>
                  <Box sx={vehicleListItemStyles.detailTitle}>Invoice</Box>
                  <Box sx={vehicleListItemStyles.detailValue}>
                    {files.filter((file: VehicleFile) => file.type === FILE_TYPE_INVOICE).length > 0 ? (
                      <CheckCircle sx={{ width: 20, height: 20 }} color="success" />
                    ) : (
                      <Cancel sx={{ width: 20, height: 20 }} color="error" />
                    )}
                  </Box>
                </Box>
              </Box>
              <Box sx={{ ...vehicleListItemStyles.detailsColumn, flex: '0.4' }}>
                <Box sx={vehicleListItemStyles.detailContainer}>
                  <Box sx={vehicleListItemStyles.detailTitle}>POA</Box>
                  <Box sx={vehicleListItemStyles.detailValue}>
                    {importer?.poa ? (
                      <CheckCircle sx={{ width: 20, height: 20 }} color="success" />
                    ) : (
                      <Cancel sx={{ width: 20, height: 20 }} color="error" />
                    )}
                  </Box>
                </Box>
                <Box sx={vehicleListItemStyles.detailContainer}>
                  <Box sx={vehicleListItemStyles.detailTitle}>Title</Box>
                  <Box sx={vehicleListItemStyles.detailValue}>
                    {files.filter((file: VehicleFile) => file.type === FILE_TYPE_TITLE).length > 0 ? (
                      <CheckCircle sx={{ width: 20, height: 20 }} color="success" />
                    ) : (
                      <Cancel sx={{ width: 20, height: 20 }} color="error" />
                    )}
                  </Box>
                </Box>
              </Box>
            </>
          ) : null}
          {!shortRepresentation &&
          !isMobileView &&
          user?.user_type === USER_TYPE_WAREHOUSE &&
          isFieldVisible('keys') ? (
            <Box sx={{ ...vehicleListItemStyles.detailsColumn, flex: '0.8' }}>
              <Box sx={vehicleListItemStyles.detailContainer}>
                <Box sx={vehicleListItemStyles.detailTitle}>Keys</Box>
                <Box sx={vehicleListItemStyles.detailValue}>{keys}</Box>
              </Box>
            </Box>
          ) : null}
          {!shortRepresentation && !isMobileView && isFieldVisible('notes') ? (
            <Box sx={{ ...vehicleListItemStyles.detailsColumn, flex: '1.3' }}>
              <Box sx={vehicleListItemStyles.detailContainer}>
                <Box sx={vehicleListItemStyles.detailTitle}>Notes</Box>
                <Box sx={(vehicleListItemStyles.detailValue, vehicleListItemStyles.comment)}>{comment || '-'}</Box>
              </Box>
            </Box>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
};

export default VehicleListItem;
